import { useLazyQuery, useMutation } from '@apollo/client';
import { track } from '@bnnvara/analytics/src/track';
import { cond, isEqual, stubTrue } from 'lodash/fp';
import PropTypes from 'prop-types';
/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';

import bookmarkVideo from '../../graphql/mutations/bookmarkVideo.graphql';
import { isBookmarked as isBookmarkedQuery } from '../../graphql/queries/isBookmarked.graphql';
import { BookmarkIconNotSaved, BookmarkIconSaved } from './BookmarkIcon';
import * as S from './styled';

// Bookmarkstates
const NOT_BOOKMARKED = 'NOT_BOOKMARKED';
const IS_BOOKMARKED = 'IS_BOOKMARKED';

const BookMarkIcon = cond([
  [(x) => isEqual(x, NOT_BOOKMARKED), () => <BookmarkIconNotSaved />],
  [(x) => isEqual(x, IS_BOOKMARKED), () => <BookmarkIconSaved />],
  [stubTrue(), () => <BookmarkIconNotSaved />],
]);

const Bookmark = ({ userId, mediaId }) => {
  const [showPopUp, setShowPopUp] = useState();
  const [stateBookmark, setStateBookmark] = useState();
  const [bookmark] = useMutation(bookmarkVideo);
  const userIsLoggedIn = !!userId;

  // If UID is defined we first check if the video is already bookmarked or not
  const [fetchQueryIsBookmarked, { data, error }] = useLazyQuery(
    isBookmarkedQuery,
    {
      variables: {
        userId,
        mediaId: Number(mediaId),
      },
    }
  );

  useEffect(() => {
    if (userIsLoggedIn) {
      fetchQueryIsBookmarked();
    } else {
      setStateBookmark(NOT_BOOKMARKED);
    }
  }, [fetchQueryIsBookmarked, userIsLoggedIn]);

  useEffect(() => {
    if (data?.me?.isBookmarked && !error) {
      setStateBookmark(IS_BOOKMARKED);
    } else {
      setStateBookmark(NOT_BOOKMARKED);
    }
  }, [data, error]);

  const showPopUpFn = () => {
    setShowPopUp(true);
    let timeOut;
    // eslint-disable-next-line prefer-const
    timeOut = setTimeout(() => {
      setShowPopUp(false);
      clearTimeout(timeOut);
    }, 10000);
  };

  const handleClick = (e) => {
    e.preventDefault();

    /**
     * If this mediaID is not in the users bookmarks
     * add bookmark to user profile
     * if it is in the bookmarks, delete it
     *
     * It takes time for the bookmark mutation to finish.
     * It will give back `true` immediately letting us know that the mutation has started
     * So it's not a comformation that the video is bookmarked.
     * We can fire of the `fetchQueryIsBookmarked` to check if the video is now bookmarked
     * But this will fail to give us back the right state because the mutation can take longer
     * to bookmark the video then the `fetchQueryIsBookmarked` to fetch to bookmark state.
     * So we only check if the bookmark is set `fetchQueryIsBookmarked` on initiation
     * and keep this as our source of truth. 😬
     */
    bookmark({
      variables: {
        userId,
        mediaId: Number(mediaId),
      },
    })
      .then((result) => {
        showPopUpFn();
        track(
          'video-player-bookmark',
          'click',
          IS_BOOKMARKED
            ? `video ${mediaId} saved on profile`
            : `video ${mediaId} deleted from profile`
        );

        const whatStateToSetBookmark =
          result?.data?.bookmarkVideo && stateBookmark !== IS_BOOKMARKED
            ? IS_BOOKMARKED
            : NOT_BOOKMARKED;

        setStateBookmark(whatStateToSetBookmark);
      })
      // eslint-disable-next-line no-shadow
      .catch((error) => {
        console.error('Bookmarking failed: ', error);
        setStateBookmark(NOT_BOOKMARKED);
      });
  };

  return (
    <>
      <S.BookmarkButton label="bookmark video" onClick={handleClick}>
        {BookMarkIcon(stateBookmark)}
      </S.BookmarkButton>
      {showPopUp && (
        <S.BookmarkPopup>
          {stateBookmark === IS_BOOKMARKED
            ? 'Toegevoegd aan '
            : 'Verwijderd uit '}
          <a
            href="https://www.bnnvara.nl/profiel"
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => {
              track('video-player-bookmark', 'click', 'bnnvara profile');
            }}
          >
            bewaarde video&apos;s
          </a>
        </S.BookmarkPopup>
      )}
    </>
  );
};

Bookmark.propTypes = {
  userId: PropTypes.string,
  mediaId: PropTypes.string.isRequired,
};

export default Bookmark;
